import { useState, useMemo } from "react";
import { Grid3X3, Plus, Filter, Search, ArrowUpDown, ChevronLeft, ChevronRight, LayoutGrid, Map, Layers } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTables, TableStatus } from "@/hooks/useTables";
import { TableFormDialog } from "@/components/tables/TableFormDialog";
import { TableCard } from "@/components/tables/TableCard";
import { TableMapView } from "@/components/tables/TableMapView";
import { BatchTableDialog } from "@/components/tables/BatchTableDialog";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type StatusFilter = "all" | TableStatus;
type SortOption = "number" | "status" | "updated";
type ViewMode = "list" | "map";

const ITEMS_PER_PAGE = 24;

const statusFilters: { value: StatusFilter; label: string; color: string }[] = [
  { value: "all", label: "Todas", color: "bg-muted hover:bg-muted/80" },
  { value: "available", label: "Disponíveis", color: "bg-emerald-500 hover:bg-emerald-600" },
  { value: "occupied", label: "Ocupadas", color: "bg-amber-500 hover:bg-amber-600" },
  { value: "reserved", label: "Reservadas", color: "bg-blue-500 hover:bg-blue-600" },
];

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "number", label: "Número" },
  { value: "status", label: "Status" },
  { value: "updated", label: "Última atualização" },
];

const statusOrder: Record<string, number> = {
  occupied: 0,
  reserved: 1,
  available: 2,
};

export default function MesasPage() {
  const navigate = useNavigate();
  const { tables, isLoading, createTable, createBatchTables, updateTable, deleteTable } = useTables();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("number");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("map");

  const handleCreate = async (data: { number: string; name?: string; capacity?: number }) => {
    const result = await createTable(data);
    if (result) setDialogOpen(false);
  };

  const handleBatchCreate = async (start: number, end: number, capacity: number) => {
    await createBatchTables(start, end, capacity);
  };

  const handleEdit = (table: any) => {
    setEditingTable(table);
    setDialogOpen(true);
  };

  const handleUpdate = async (data: { number: string; name?: string; capacity?: number }) => {
    if (!editingTable) return;
    const result = await updateTable(editingTable.id, data);
    if (result) {
      setDialogOpen(false);
      setEditingTable(null);
    }
  };

  const handleToggleActive = async (table: any) => {
    await updateTable(table.id, { is_active: !table.is_active });
  };

  const handleDelete = async (id: string) => {
    await deleteTable(id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTable(null);
  };

  const handleSelectTableFromMap = (table: any) => {
    if (table.status === "occupied") {
      // Navigate to consumo aberto with this table
      navigate("/dashboard/consumo-aberto");
    } else {
      // Navigate to PDV with this table pre-selected
      toast.info(`Mesa ${table.number} selecionada`, {
        description: "Vá ao PDV para iniciar um pedido nesta mesa.",
      });
    }
  };

  const activeTables = useMemo(() => tables.filter((t) => t.is_active), [tables]);
  const inactiveTables = useMemo(() => tables.filter((t) => !t.is_active), [tables]);

  const filteredActiveTables = useMemo(() => {
    let filtered = activeTables;
    
    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter((t) => 
        t.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "number":
          return a.number.localeCompare(b.number, undefined, { numeric: true });
        case "status":
          return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        case "updated":
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [activeTables, statusFilter, searchQuery, sortBy]);

  // Reset page when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredActiveTables.length / ITEMS_PER_PAGE);
  const paginatedTables = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredActiveTables.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActiveTables, currentPage]);

  // Count by status
  const statusCounts = useMemo(() => ({
    all: activeTables.length,
    available: activeTables.filter((t) => t.status === "available").length,
    occupied: activeTables.filter((t) => t.status === "occupied").length,
    reserved: activeTables.filter((t) => t.status === "reserved").length,
  }), [activeTables]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <Grid3X3 className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Mesas</h1>
            <p className="text-sm text-muted-foreground">Gerencie as mesas do restaurante</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="h-9">
              <TabsTrigger value="map" className="gap-2 px-3">
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">Mapa</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="gap-2 px-3">
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Lista</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" className="gap-2" onClick={() => setBatchDialogOpen(true)}>
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Criar em Lote</span>
          </Button>
          <Button className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Nova Mesa
          </Button>
        </div>
      </div>

      {/* Map View */}
      {viewMode === "map" && (
        <TableMapView
          tables={tables}
          onSelectTable={handleSelectTableFromMap}
          onEditTable={handleEdit}
        />
      )}

      {/* List View */}
      {viewMode === "list" && (
        <>
          {/* Search, Sort and Filters */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar mesa..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-4 w-4 text-muted-foreground" />
              {statusFilters.map((filter) => (
                <Badge
                  key={filter.value}
                  variant={statusFilter === filter.value ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer transition-all",
                    statusFilter === filter.value 
                      ? filter.value === "all" 
                        ? "bg-foreground text-background" 
                        : `${filter.color} text-white border-transparent`
                      : "hover:bg-muted"
                  )}
                  onClick={() => setStatusFilter(filter.value)}
                >
                  {filter.label} ({statusCounts[filter.value]})
                </Badge>
              ))}
            </div>
          </div>

          {/* Active Tables */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              {statusFilter === "all" 
                ? `Mesas Ativas (${filteredActiveTables.length})`
                : `${statusFilters.find(f => f.value === statusFilter)?.label} (${filteredActiveTables.length})`
              }
            </h2>
            {filteredActiveTables.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Grid3X3 className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">
                    {statusFilter === "all" 
                      ? "Nenhuma mesa cadastrada ainda.\nClique em \"Nova Mesa\" para começar."
                      : `Nenhuma mesa ${statusFilters.find(f => f.value === statusFilter)?.label.toLowerCase()}.`
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {paginatedTables.map((table) => (
                    <TableCard
                      key={table.id}
                      table={table}
                      onEdit={() => handleEdit(table)}
                      onToggleActive={() => handleToggleActive(table)}
                      onDelete={() => handleDelete(table.id)}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground ml-2">
                      {filteredActiveTables.length} mesas
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Inactive Tables */}
          {inactiveTables.length > 0 && statusFilter === "all" && (
            <div>
              <h2 className="text-lg font-semibold mb-4 text-muted-foreground">
                Mesas Inativas ({inactiveTables.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {inactiveTables.map((table) => (
                  <TableCard
                    key={table.id}
                    table={table}
                    onEdit={() => handleEdit(table)}
                    onToggleActive={() => handleToggleActive(table)}
                    onDelete={() => handleDelete(table.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Form Dialog */}
      <TableFormDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={editingTable ? handleUpdate : handleCreate}
        initialData={editingTable}
      />

      {/* Batch Create Dialog */}
      <BatchTableDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        onSubmit={handleBatchCreate}
      />
    </div>
  );
}
